<template>
  <div class="invoice-template-picker">
    <template v-if="isCustomizing">
      <div class="invoice-template-editor-header">
        <button type="button" class="btn btn-light btn-sm" @click="showTemplatePicker">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{ $t('back_to_templates') }}
        </button>
        <div class="min-width-0">
          <div class="text-muted text-uppercase small fw-bold">{{ $t('customize') }}</div>
          <div class="fw-bold text-truncate">{{ selectedTemplate.name }}</div>
        </div>
      </div>

      <invoice-template-builder
        :key="builderKey"
        class="invoice-template-customizer"
        :starter-template="selectedTemplate.template"
        :starter-template-key="selectedTemplate.presetKey"
        :business-override="businessOverride"
        :admin-account-id="adminAccountId"
        @templateSaved="handleTemplateSaved"
      />
    </template>

    <template v-else>
      <div class="invoice-template-picker-header">
        <div>
          <h5 class="mb-1 fw-bold">{{ $t('invoice_templates') }}</h5>
          <p class="mb-0 text-muted small">{{ $t('invoice_templates_description') }}</p>
        </div>
        <div class="invoice-template-picker-actions">
          <button
            type="button"
            class="btn btn-primary rounded-pill px-4 fw-bold"
            data-testid="customize-invoice-template"
            :disabled="loading || saving"
            @click="customizeSelectedTemplate"
          >
            <AppIcon name="bi-sliders" class="bi bi-sliders me-2" />{{ $t('customize') }}
          </button>
          <button
            v-if="hasUnsavedSelection"
            type="button"
            class="btn btn-success rounded-pill px-4 fw-bold"
            data-testid="save-selected-invoice-template"
            :disabled="loading || saving"
            @click="saveSelectedTemplate"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <AppIcon name="bi-check-circle" v-else class="bi bi-check-circle me-2" />{{ saving ? $t('saving') : $t('save') }}
          </button>
        </div>
      </div>

      <div class="invoice-template-preview-area">
        <div v-if="loading" class="invoice-template-loading">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $t('loading') }}</span>
          </div>
        </div>
        <div v-else class="invoice-template-preview-canvas" data-testid="invoice-template-preview" aria-live="polite">
          <div class="invoice-template-preview-paper" v-html="selectedTemplate.previewHtml"></div>
        </div>
      </div>

      <div class="invoice-template-strip">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <div>
            <div class="fw-bold small">{{ $t('select_template') }}</div>
            <div class="text-muted" style="font-size: 0.72rem;">{{ selectedTemplate.name }}</div>
          </div>
          <span class="badge rounded-pill bg-light text-dark border">{{ templateChoices.length }}</span>
        </div>

        <div class="invoice-template-cards">
          <button
            v-for="templateOption in templateChoices"
            :key="templateOption.key"
            type="button"
            class="invoice-template-card"
            :class="{ 'is-selected': selectedTemplateKey === templateOption.key }"
            :data-testid="`invoice-template-card-${templateOption.key}`"
            :aria-label="templateOption.name"
            :aria-pressed="selectedTemplateKey === templateOption.key ? 'true' : 'false'"
            @click="selectedTemplateKey = templateOption.key"
          >
            <span class="invoice-template-card-preview">
              <span class="invoice-template-card-paper" v-html="templateOption.previewHtml"></span>
            </span>
            <span class="invoice-template-card-label">
              <span class="text-truncate">{{ templateOption.name }}</span>
              <AppIcon name="bi-check-circle-fill" v-if="selectedTemplateKey === templateOption.key" class="bi bi-check-circle-fill" />
            </span>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import helper from '../helper'
import defaultTemplate from '../defaultTemplate.json'
import minimalTemplate from '../template_minimal.json'
import modernTemplate from '../template_modern.json'
import elegantTemplate from '../template_elegant.json'
import boldTemplate from '../template_bold.json'
import cleanTemplate from '../template_clean.json'
import executiveTemplate from '../template_executive.json'
import tealTemplate from '../template_teal.json'
import doliTemplate from '../template_doli.json'
import professionalInvoicePresets from '../invoiceProfessionalPresets'
import autoEntrepreneurTemplate from '../invoiceAutoEntrepreneurTemplate'
import InvoiceTemplateBuilder from './InvoiceTemplateBuilder.vue'

const PRESET_TEMPLATES = [
  { key: 'default', name: 'Classic', template: defaultTemplate },
  { key: 'minimal', name: 'Minimalist', template: minimalTemplate },
  { key: 'modern', name: 'Modern', template: modernTemplate },
  { key: 'elegant', name: 'Elegant', template: elegantTemplate },
  { key: 'bold', name: 'Bold', template: boldTemplate },
  { key: 'clean', name: 'Clean', template: cleanTemplate },
  { key: 'executive', name: 'Executive', template: executiveTemplate },
  { key: 'teal', name: 'Teal Professional', template: tealTemplate },
  { key: 'doli', name: 'Doli', template: doliTemplate },
  { key: 'autoEntrepreneur', nameKey: 'auto_entrepreneur_template', template: autoEntrepreneurTemplate },
  { key: 'corporateNavy', name: 'Corporate Navy', template: professionalInvoicePresets.corporateNavy },
  { key: 'financeGreen', name: 'Finance Green', template: professionalInvoicePresets.financeGreen },
  { key: 'burgundyClassic', name: 'Burgundy Classic', template: professionalInvoicePresets.burgundyClassic },
  { key: 'sandstone', name: 'Sandstone Studio', template: professionalInvoicePresets.sandstone }
]

export default {
  name: 'InvoiceTemplatePicker',
  components: {
    InvoiceTemplateBuilder
  },
  props: {
    businessOverride: {
      type: Object,
      default: null
    },
    adminAccountId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: true,
      saving: false,
      isCustomizing: false,
      selectedTemplateKey: 'current',
      currentTemplate: defaultTemplate,
      currentTemplateId: null,
      builderKey: 0
    }
  },
  computed: {
    business() {
      return this.businessOverride || this.$store.getters.BUSINESS || {}
    },
    templateChoices() {
      const current = {
        key: 'current',
        presetKey: 'default',
        name: this.$t('current_design'),
        template: this.currentTemplate
      }

      return [current, ...PRESET_TEMPLATES.map(option => ({
        ...option,
        name: option.nameKey ? this.$t(option.nameKey) : option.name,
        presetKey: option.key
      }))].map(option => ({
        ...option,
        previewHtml: this.formatPreview(option.template)
      }))
    },
    selectedTemplate() {
      return this.templateChoices.find(option => option.key === this.selectedTemplateKey) || this.templateChoices[0]
    },
    hasUnsavedSelection() {
      return this.selectedTemplateKey !== 'current'
    }
  },
  methods: {
    placeholderLogo() {
      const businessName = this.business.name || 'Gstocky'
      const initial = businessName.trim().charAt(0).toUpperCase() || 'G'
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="180" height="80" viewBox="0 0 180 80"><rect width="180" height="80" rx="12" fill="#f1f5f9"/><circle cx="40" cy="40" r="25" fill="#2563eb"/><text x="40" y="49" text-anchor="middle" font-family="Arial" font-size="26" font-weight="700" fill="white">${initial}</text><text x="75" y="46" font-family="Arial" font-size="18" font-weight="700" fill="#1e293b">${businessName.slice(0, 10)}</text></svg>`
      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
    },
    formatPreview(template) {
      if (!template) return ''

      const isAutoEntrepreneur = template.attributes && template.attributes['data-tax-regime'] === 'auto-entrepreneur'
      const previewTotal = isAutoEntrepreneur ? 10000 : 12000

      const parser = new DOMParser()
      const previewDocument = parser.parseFromString(helper.jsonToHtml(template), 'text/html')

      previewDocument.querySelectorAll('[data-invoicly-display]').forEach(element => {
        const displayValues = (element.getAttribute('data-invoicly-display') || 'always')
          .split(',')
          .map(value => value.trim().toLowerCase())
        if (!displayValues.includes('always') && !displayValues.includes('invoice')) element.remove()
      })

      const firstProductRow = previewDocument.querySelector('tbody tr')
      if (firstProductRow && firstProductRow.parentNode && firstProductRow.parentNode.children.length === 1) {
        firstProductRow.parentNode.appendChild(firstProductRow.cloneNode(true))
      }

      const currency = this.business.currency || 'MAD'
      const money = value => helper.formatMoney(value, currency)
      const replacements = {
        '{business.name}': this.business.name || 'Atlas Studio',
        '{business.logo}': this.business.logo || this.placeholderLogo(),
        '{business.electronic_stamp}': this.business.electronic_stamp || '',
        '{business.currency}': currency,
        '{business.email}': this.business.email || 'hello@atlas-studio.com',
        '{business.address}': this.business.address || 'Casablanca, Morocco',
        '{business.phone}': this.business.phone || '+212 5 00 00 00 00',
        '{business.website}': this.business.website || 'atlas-studio.com',
        '{business.billing_info}': this.business.billing_info || 'Bank details · MA64 0000 0000 0000',
        '{document.type}': String(this.$t('invoice')).toUpperCase(),
        '{document.reference}': 'INV-2026-001',
        '{document.date}': '29/09/2026',
        '{receiver.name}': 'Northwind Company',
        '{receiver.reference}': 'CL-001',
        '{receiver.address}': '12 Market Street, Casablanca',
        '{receiver.city}': 'Casablanca',
        '{receiver.vat_number}': 'ICE 001234567890',
        '{client.name}': 'Northwind Company',
        '{client.reference}': 'CL-001',
        '{client.address}': '12 Market Street, Casablanca',
        '{supplier.name}': 'Atlas Studio',
        '{supplier.reference}': 'SUP-001',
        '{supplier.address}': 'Casablanca, Morocco',
        '{product.index}': '1',
        '{product.label}': 'Professional services package',
        '{product.quantity}': '2',
        '{product.unity}': this.$t('unity'),
        '{product.price}': money(5000),
        '{product.vat}': isAutoEntrepreneur ? '0%' : '20%',
        '{product.sub_total}': money(10000),
        '{product.total}': money(previewTotal),
        '{sub_total}': money(10000),
        '{net_ht_after_discount}': money(10000),
        '{total_tax}': money(isAutoEntrepreneur ? 0 : 2000),
        '{total_discount}': money(0),
        '{total}': money(previewTotal),
        '{notes}': this.$t('thank_you'),
        '{payment_term}': 'Payment due within 30 days',
        '{client_po_number}': 'PO-1042',
        '{client_dn_number}': 'DN-1042',
        '{client_in_number}': 'IN-1042',
        '{{currency}}': currency,
        '#product_label#': this.$t('product_label'),
        '#product_quantity#': this.$t('product_quantity'),
        '#product_unit_price#': this.$t('product_unit_price'),
        '#product_vat#': this.$t('product_vat'),
        '#product_total_ht#': this.$t('product_total_ht'),
        '#product_total#': this.$t('total'),
        '#auto_entrepreneur#': this.$t('auto_entrepreneur'),
        '#vat_not_applicable_auto_entrepreneur#': this.$t('vat_not_applicable_auto_entrepreneur'),
        '#subtotal#': this.$t('subtotal'),
        '#vat#': this.$t('vat'),
        '#discount#': this.$t('discount'),
        '#total#': this.$t('total'),
        '#amount_in_words#': this.$t('amount_in_words'),
        '#date#': this.$t('date'),
        '#u_p#': this.$t('u_p'),
        '#sub_total#': this.$t('sub_total'),
        '#net_ht_after_discount#': this.$t('net_ht_after_discount'),
        '#product_index#': '#',
        '#client_signature#': this.$t('client_signature'),
        '#supplier_signature#': this.$t('supplier_signature'),
        '#electronic_stamp#': this.$t('electronic_stamp')
      }

      let html = previewDocument.body.innerHTML
      Object.entries(replacements).forEach(([placeholder, value]) => {
        html = html.split(placeholder).join(value || '')
      })

      return html
        .replace(/\{letterToNumbers\([^)]+\)\}/g, money(previewTotal))
        .replace(/\{business\.[^}]+\}/g, '')
        .replace(/\{[^{}]+\}/g, '')
    },
    async loadCurrentTemplate() {
      this.loading = true
      try {
        const endpoint = this.adminAccountId
          ? `${process.env.VUE_APP_API}/admin/accounts/${this.adminAccountId}/invoice-template`
          : `${process.env.VUE_APP_API}/templates-by-type?type=standard`
        const response = await helper.customGet(endpoint, { silent: true })
        if (response.data && response.data.data) {
          this.currentTemplateId = response.data.id
          this.currentTemplate = JSON.parse(response.data.data)
        } else {
          this.currentTemplateId = null
        }
      } catch (error) {
        this.currentTemplate = defaultTemplate
      } finally {
        this.loading = false
      }
    },
    customizeSelectedTemplate() {
      this.builderKey += 1
      this.isCustomizing = true
    },
    async saveSelectedTemplate() {
      if (!this.hasUnsavedSelection) return

      this.saving = true
      try {
        const data = JSON.stringify(this.selectedTemplate.template)
        let response
        if (this.adminAccountId) {
          response = await helper.customPatch(
            `${process.env.VUE_APP_API}/admin/accounts/${this.adminAccountId}/invoice-template`,
            { data },
            { showToast: true }
          )
        } else {
          response = this.currentTemplateId
            ? await helper.customPatch(
              `${process.env.VUE_APP_API}/templates/${this.currentTemplateId}`,
              { data },
              { showToast: true }
            )
            : await helper.customPost(
              `${process.env.VUE_APP_API}/templates`,
              { type: 'standard', data },
              { showToast: true }
            )
        }

        this.currentTemplateId = response.data.id
        this.currentTemplate = this.selectedTemplate.template
        this.selectedTemplateKey = 'current'
        this.$emit('templateSaved')
      } finally {
        this.saving = false
      }
    },
    async showTemplatePicker() {
      this.isCustomizing = false
      this.selectedTemplateKey = 'current'
      await this.loadCurrentTemplate()
    },
    handleTemplateSaved() {
      this.$emit('templateSaved')
    }
  },
  mounted() {
    this.loadCurrentTemplate()
  }
}
</script>

<style>
.invoice-template-picker {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 70vh;
  overflow: hidden;
  background: #f8fafc;
}

.invoice-template-picker-header,
.invoice-template-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex: 0 0 auto;
  padding: 1rem 1.25rem;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.invoice-template-editor-header {
  justify-content: flex-start;
  padding: 0.65rem 1rem;
}

.invoice-template-picker-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.invoice-template-preview-area {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 1.25rem;
  background-color: #eef2f7;
  background-image: radial-gradient(#cbd5e1 0.7px, transparent 0.7px);
  background-size: 16px 16px;
}

.invoice-template-loading {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(248, 250, 252, 0.85);
  z-index: 2;
}

.invoice-template-preview-canvas {
  position: relative;
  flex: 0 0 460px;
  width: 460px;
  height: 605px;
}

.invoice-template-preview-paper {
  position: absolute;
  top: 0;
  left: 50%;
  width: 794px;
  min-height: 1040px;
  padding: 50px;
  overflow: hidden;
  transform: translateX(-50%) scale(0.58);
  transform-origin: top center;
  background: #fff;
  border: 1px solid #dbe3ec;
  box-shadow: 0 22px 55px rgba(15, 23, 42, 0.18);
  color: #0f172a;
}

.invoice-template-preview-paper .table-responsive,
.invoice-template-card-paper .table-responsive {
  overflow: visible !important;
}

.invoice-template-preview-paper img,
.invoice-template-card-paper img {
  max-width: 150px;
  max-height: 90px;
  object-fit: contain;
}

.invoice-template-strip {
  flex: 0 0 auto;
  padding: 0.75rem 1rem 1rem;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -10px 30px rgba(15, 23, 42, 0.05);
}

.invoice-template-cards {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding: 0.15rem 0.1rem 0.35rem;
  scroll-snap-type: x proximity;
  scrollbar-width: thin;
}

.invoice-template-card {
  flex: 0 0 148px;
  width: 148px;
  padding: 6px;
  overflow: hidden;
  text-align: left;
  scroll-snap-align: start;
  background: #fff;
  border: 2px solid transparent;
  border-radius: 12px;
  color: #334155;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.invoice-template-card:hover {
  border-color: #bfdbfe;
  transform: translateY(-2px);
  box-shadow: 0 7px 18px rgba(37, 99, 235, 0.1);
}

.invoice-template-card.is-selected {
  border-color: #2563eb;
  box-shadow: 0 7px 18px rgba(37, 99, 235, 0.16);
}

.invoice-template-card-preview {
  position: relative;
  display: block;
  width: 132px;
  height: 88px;
  overflow: hidden;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.invoice-template-card-paper {
  position: absolute;
  top: 0;
  left: 50%;
  display: block;
  width: 794px;
  min-height: 1040px;
  padding: 50px;
  transform: translateX(-50%) scale(0.166);
  transform-origin: top center;
  background: #fff;
  pointer-events: none;
}

.invoice-template-card-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  padding: 0.55rem 0.25rem 0.15rem;
  font-size: 0.76rem;
  font-weight: 700;
}

.invoice-template-card-label .bi {
  flex: 0 0 auto;
  color: #2563eb;
}

.invoice-template-customizer {
  flex: 1 1 auto;
  min-height: 0;
  height: auto !important;
}

@media (max-width: 767.98px) {
  .invoice-template-picker-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .invoice-template-picker-header .btn {
    flex: 1 1 auto;
  }

  .invoice-template-picker-actions {
    width: 100%;
  }

  .invoice-template-preview-area {
    padding: 0.75rem;
  }

  .invoice-template-preview-canvas {
    flex-basis: 349px;
    width: 349px;
    height: 468px;
  }

  .invoice-template-preview-paper {
    transform: translateX(-50%) scale(0.44);
  }
}
</style>
